import { useState, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Loader2, Folder, FolderOpen, FileText, Upload, ChevronRight, ChevronDown,
  MoreHorizontal, ExternalLink, Move, Trash2, AlertCircle, HardDrive,
  ArrowLeft, CheckCircle,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  SVNode, SVStagedUpload,
  getSmartVaultVault,
  getSmartVaultStagedUploads,
  deleteSmartVaultFile,
  uploadToSmartVault,
  moveStagedToSmartVault,
  moveSmartVaultFile,
  getSmartVaultFileUrl,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtBytes(n: number | null | undefined) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(s: string | null | undefined) {
  if (!s) return "—";
  try { return format(parseISO(s), "MMM d, yyyy"); } catch { return s; }
}

// ── Folder picker dialog ─────────────────────────────────────────────────────

interface FolderPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (folderUri: string, folderPath: string) => void;
  folders: FlatFolder[];
  title: string;
  confirmLabel: string;
  loading?: boolean;
}

interface FlatFolder {
  uri: string;
  name: string;
  depth: number;
  path: string;
}

function FolderPickerDialog({ open, onClose, onConfirm, folders, title, confirmLabel, loading }: FolderPickerProps) {
  const [selected, setSelected] = useState<FlatFolder | null>(null);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setSelected(null); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-72 border rounded-md">
          {folders.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">No folders available — expand folders in the vault browser first.</p>
          ) : (
            <div className="p-1 space-y-0.5">
              {folders.map((f) => (
                <button
                  key={f.uri}
                  onClick={() => setSelected(f)}
                  className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors ${selected?.uri === f.uri ? "bg-primary/10 text-primary" : ""}`}
                  style={{ paddingLeft: `${8 + f.depth * 16}px` }}
                >
                  <Folder className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <span className="truncate">{f.name}</span>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
        {selected && (
          <p className="text-xs text-muted-foreground truncate">Selected: <span className="font-mono">{selected.path}</span></p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); setSelected(null); }}>Cancel</Button>
          <Button disabled={!selected || loading} onClick={() => selected && onConfirm(selected.uri, selected.path)}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Tree node types ───────────────────────────────────────────────────────────

interface TreeNode extends SVNode {
  children?: TreeNode[];
  childrenLoaded?: boolean;
  expanded?: boolean;
}

// ── Main panel ────────────────────────────────────────────────────────────────

interface SmartVaultPanelProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  clientName: string;
  svClientId: string | null | undefined;
}

export function SmartVaultPanel({ open, onClose, accountId, clientName, svClientId }: SmartVaultPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Selected folder for uploads
  const [selectedFolder, setSelectedFolder] = useState<TreeNode | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<{ name: string; uri: string }[]>([]);

  // Tree state (mutable ref to avoid re-renders on every expand)
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);

  // Dialog states
  const [moveFileDialog, setMoveFileDialog] = useState<{ node: TreeNode } | null>(null);
  const [moveStagedDialog, setMoveStagedDialog] = useState<{ upload: SVStagedUpload } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ node: TreeNode } | null>(null);
  const [stagedExpanded, setStagedExpanded] = useState(true);

  // Upload state
  const [uploading, setUploading] = useState(false);

  // ── Root vault query ──────────────────────────────────────────────────────
  const { isLoading: rootLoading, error: rootError } = useQuery<SVNode[]>({
    queryKey: ["sv-vault-root", accountId],
    queryFn: async () => {
      const nodes = await getSmartVaultVault(accountId);
      setTree(nodes.map((n) => ({ ...n, children: [], childrenLoaded: false, expanded: false })));
      return nodes;
    },
    enabled: open && !!svClientId,
    staleTime: 60_000,
    retry: false,
  });

  // ── Staged uploads query ──────────────────────────────────────────────────
  const { data: stagedUploads = [], isLoading: stagedLoading } = useQuery<SVStagedUpload[]>({
    queryKey: ["sv-staged", accountId],
    queryFn: () => getSmartVaultStagedUploads(accountId),
    enabled: open && !!svClientId,
    staleTime: 30_000,
  });

  // ── Collect all known folders (for folder picker) ─────────────────────────
  const collectFolders = useCallback((nodes: TreeNode[], depth = 0, prefix = ""): FlatFolder[] => {
    const result: FlatFolder[] = [];
    for (const n of nodes) {
      if (n.type === "folder") {
        const path = prefix ? `${prefix} / ${n.name}` : n.name;
        result.push({ uri: n.uri, name: n.name, depth, path });
        if (n.children && n.childrenLoaded) {
          result.push(...collectFolders(n.children, depth + 1, path));
        }
      }
    }
    return result;
  }, []);

  const allFolders = collectFolders(tree);

  // ── Lazy-load folder children ─────────────────────────────────────────────
  const expandFolder = useCallback(async (node: TreeNode) => {
    if (node.childrenLoaded) {
      // toggle only
      setTree((prev) => toggleExpanded(prev, node.uri));
      return;
    }
    setTree((prev) => setNodeLoading(prev, node.uri, true));
    try {
      const children = await getSmartVaultVault(accountId, node.uri);
      const childNodes: TreeNode[] = children.map((c) => ({
        ...c, children: [], childrenLoaded: false, expanded: false,
      }));
      setTree((prev) => setNodeChildren(prev, node.uri, childNodes));
    } catch (err: any) {
      toast({ title: "Failed to load folder", description: err.message, variant: "destructive" });
      setTree((prev) => setNodeLoading(prev, node.uri, false));
    }
  }, [accountId, toast]);

  // ── Tree mutation helpers ─────────────────────────────────────────────────
  function toggleExpanded(nodes: TreeNode[], uri: string): TreeNode[] {
    return nodes.map((n) => {
      if (n.uri === uri) return { ...n, expanded: !n.expanded };
      if (n.children) return { ...n, children: toggleExpanded(n.children, uri) };
      return n;
    });
  }

  function setNodeLoading(nodes: TreeNode[], uri: string, loading: boolean): TreeNode[] {
    return nodes.map((n) => {
      if (n.uri === uri) return { ...n, _loading: loading } as any;
      if (n.children) return { ...n, children: setNodeLoading(n.children, uri, loading) };
      return n;
    });
  }

  function setNodeChildren(nodes: TreeNode[], uri: string, children: TreeNode[]): TreeNode[] {
    return nodes.map((n) => {
      if (n.uri === uri) return { ...n, children, childrenLoaded: true, expanded: true, _loading: false } as any;
      if (n.children) return { ...n, children: setNodeChildren(n.children, uri, children) };
      return n;
    });
  }

  function removeNode(nodes: TreeNode[], uri: string): TreeNode[] {
    return nodes
      .filter((n) => n.uri !== uri)
      .map((n) => n.children ? { ...n, children: removeNode(n.children, uri) } : n);
  }

  // ── File actions ──────────────────────────────────────────────────────────

  const openFile = async (node: TreeNode) => {
    try {
      const url = await getSmartVaultFileUrl(accountId, node.uri);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      // Fallback: try downloadUri directly
      if (node.downloadUri) {
        window.open(node.downloadUri, "_blank", "noopener,noreferrer");
      } else {
        toast({ title: "Cannot open file", description: err.message, variant: "destructive" });
      }
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (uri: string) => deleteSmartVaultFile(accountId, uri),
    onSuccess: (_, uri) => {
      setTree((prev) => removeNode(prev, uri));
      toast({ title: "File deleted" });
      setDeleteConfirm(null);
    },
    onError: (err: any) => toast({ title: "Delete failed", description: err.message, variant: "destructive" }),
  });

  const moveMutation = useMutation({
    mutationFn: ({ fileUri, targetFolderUri }: { fileUri: string; targetFolderUri: string }) =>
      moveSmartVaultFile(fileUri, targetFolderUri),
    onSuccess: () => {
      // Reload root to reflect new structure
      queryClient.invalidateQueries({ queryKey: ["sv-vault-root", accountId] });
      toast({ title: "File moved" });
      setMoveFileDialog(null);
    },
    onError: (err: any) => toast({ title: "Move failed", description: err.message, variant: "destructive" }),
  });

  const moveStagedMutation = useMutation({
    mutationFn: ({ attachmentId, folderPath }: { attachmentId: string; folderPath: string }) =>
      moveStagedToSmartVault(accountId, attachmentId, folderPath),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sv-staged", accountId] });
      queryClient.invalidateQueries({ queryKey: ["sv-vault-root", accountId] });
      toast({ title: "Upload moved to SmartVault" });
      setMoveStagedDialog(null);
    },
    onError: (err: any) => toast({ title: "Move failed", description: err.message, variant: "destructive" }),
  });

  const handleUpload = async (file: File) => {
    if (!selectedFolder) {
      toast({ title: "Select a folder first", description: "Click a folder to select it as the upload destination.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      await uploadToSmartVault(accountId, selectedFolder.uri, file);
      // Reload the selected folder's children
      if (selectedFolder.childrenLoaded) {
        const fresh = await getSmartVaultVault(accountId, selectedFolder.uri);
        setTree((prev) => setNodeChildren(prev, selectedFolder.uri, fresh.map((c) => ({ ...c, children: [], childrenLoaded: false, expanded: false }))));
      }
      toast({ title: "File uploaded", description: `"${file.name}" added to ${selectedFolder.name}` });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  // ── Render tree nodes ─────────────────────────────────────────────────────
  const renderNode = (node: TreeNode & { _loading?: boolean }, depth = 0) => {
    const isFolder = node.type === "folder";
    const isSelected = selectedFolder?.uri === node.uri;

    return (
      <div key={node.uri}>
        <div
          className={`flex items-center gap-0 group text-sm hover:bg-muted/60 rounded transition-colors ${isSelected ? "bg-primary/10" : ""}`}
          style={{ paddingLeft: `${8 + depth * 20}px` }}
        >
          {/* Expand toggle or spacer */}
          {isFolder ? (
            <button
              className="p-1 shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => expandFolder(node)}
            >
              {node._loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : node.expanded ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
          ) : (
            <span className="w-[22px] shrink-0" />
          )}

          {/* Icon */}
          {isFolder ? (
            node.expanded
              ? <FolderOpen className="w-4 h-4 text-amber-500 shrink-0 mr-1.5" />
              : <Folder className="w-4 h-4 text-amber-500 shrink-0 mr-1.5" />
          ) : (
            <FileText className="w-4 h-4 text-blue-500 shrink-0 mr-1.5" />
          )}

          {/* Name — clicking folder selects it, file does nothing inline */}
          <button
            className={`flex-1 text-left truncate py-1.5 pr-1 ${isFolder ? "font-medium" : ""}`}
            onClick={() => {
              if (isFolder) {
                setSelectedFolder(isSelected ? null : node);
                if (!node.expanded) expandFolder(node);
              } else {
                openFile(node);
              }
            }}
          >
            {node.name}
          </button>

          {/* Columns */}
          <span className="hidden lg:block w-20 text-right text-xs text-muted-foreground pr-2 shrink-0">{fmtBytes(node.size)}</span>
          <span className="hidden xl:block w-28 text-right text-xs text-muted-foreground pr-2 shrink-0">{node.modifiedBy ?? "—"}</span>
          <span className="hidden xl:block w-28 text-right text-xs text-muted-foreground pr-2 shrink-0">{fmtDate(node.modifiedOn)}</span>
          <span className="hidden 2xl:block w-28 text-right text-xs text-muted-foreground pr-2 shrink-0">{fmtDate(node.createdOn)}</span>
          <span className="hidden lg:block w-16 text-right text-xs text-muted-foreground pr-2 shrink-0 capitalize">{node.type}</span>

          {/* Actions */}
          {!isFolder && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-90 transition-opacity shrink-0 mr-1">
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openFile(node)}>
                  <ExternalLink className="w-3.5 h-3.5 mr-2" /> Open
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMoveFileDialog({ node })}>
                  <Move className="w-3.5 h-3.5 mr-2" /> Move To…
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setDeleteConfirm({ node })}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isFolder && (
            <span className="w-7 shrink-0" /> // balance width with file action button
          )}
        </div>

        {/* Children */}
        {isFolder && node.expanded && node.children && (
          <div>
            {node.children.length === 0 && node.childrenLoaded ? (
              <p className="text-xs text-muted-foreground italic py-1" style={{ paddingLeft: `${28 + (depth + 1) * 20}px` }}>Empty folder</p>
            ) : (
              node.children.map((child) => renderNode(child as any, depth + 1))
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Column header ─────────────────────────────────────────────────────────
  const ColumnHeader = () => (
    <div className="flex items-center gap-0 text-xs text-muted-foreground border-b pb-1 mb-0.5 px-2">
      <span className="flex-1">Name</span>
      <span className="hidden lg:block w-20 text-right pr-2 shrink-0">Size</span>
      <span className="hidden xl:block w-28 text-right pr-2 shrink-0">Modified By</span>
      <span className="hidden xl:block w-28 text-right pr-2 shrink-0">Modified On</span>
      <span className="hidden 2xl:block w-28 text-right pr-2 shrink-0">Created On</span>
      <span className="hidden lg:block w-16 text-right pr-2 shrink-0">Type</span>
      <span className="w-7 shrink-0" />
    </div>
  );

  // ── Not provisioned / not configured states ───────────────────────────────
  const renderNotProvisionedState = () => (
    <div className="flex flex-col items-center justify-center flex-1 px-8 py-12">
      <div className="w-full max-w-sm rounded-xl border bg-card shadow-sm p-8 flex flex-col items-center gap-5 text-center">
        {/* Icon + title */}
        <div className="flex flex-col items-center gap-2">
          <div className="rounded-full bg-muted p-4">
            <HardDrive className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold tracking-tight">SmartVault</h3>
          <p className="text-sm text-muted-foreground">Document management integration</p>
        </div>

        {/* Feature list */}
        <ul className="w-full space-y-2 text-left">
          {[
            "Folder tree browser — navigate client document folders",
            "File actions — open, move, delete directly from CentCom",
            "Portal uploads — clients upload; files land in SmartVault automatically",
            "Staged uploads — queue files before moving to vault",
          ].map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-foreground">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* Status note */}
        <p className="text-xs text-muted-foreground italic border-t pt-4 w-full text-center">
          Awaiting SmartVault credentials — contact Wade to activate
        </p>
      </div>
    </div>
  );

  const renderErrorState = (err: Error) => (
    <div className="flex flex-col items-center justify-center h-60 gap-3 text-center px-8">
      <AlertCircle className="w-10 h-10 text-destructive" />
      <p className="text-sm font-medium text-destructive">Failed to connect to SmartVault</p>
      <p className="text-xs text-muted-foreground">{err.message}</p>
      {err.message.includes("configured") && (
        <p className="text-xs text-muted-foreground">Check that SV_CLIENT_ID, SV_PRIVATE_KEY, SV_ADMIN_EMAIL, and SV_ACCOUNT_ID are set in the server .env file.</p>
      )}
    </div>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent
          side="right"
          className="w-[900px] max-w-[95vw] flex flex-col p-0 gap-0"
        >
          {/* Header */}
          <SheetHeader className="px-4 py-3 border-b shrink-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <HardDrive className="w-4 h-4 text-muted-foreground shrink-0" />
                <SheetTitle className="truncate text-base">SmartVault — {clientName}</SheetTitle>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {selectedFolder && (
                  <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                    Upload to: <span className="font-medium text-foreground">{selectedFolder.name}</span>
                  </span>
                )}
                <Button
                  size="sm"
                  variant={selectedFolder ? "default" : "outline"}
                  className="gap-1.5"
                  disabled={!selectedFolder || uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Upload className="w-3.5 h-3.5" />}
                  Upload
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
            {selectedFolder && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <button
                  className="hover:text-foreground flex items-center gap-1"
                  onClick={() => setSelectedFolder(null)}
                >
                  <ArrowLeft className="w-3 h-3" /> Deselect folder
                </button>
              </div>
            )}
          </SheetHeader>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {!svClientId ? (
              renderNotProvisionedState()
            ) : rootError ? (
              renderErrorState(rootError as Error)
            ) : (
              <ScrollArea className="flex-1">
                <div className="p-3">
                  {/* Column headers */}
                  <ColumnHeader />

                  {/* Tree */}
                  {rootLoading ? (
                    <div className="flex items-center justify-center h-24 gap-2 text-muted-foreground text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading vault…
                    </div>
                  ) : tree.length === 0 ? (
                    <div className="text-center py-8 text-sm text-muted-foreground italic">Vault is empty</div>
                  ) : (
                    <div className="space-y-0">
                      {tree.map((node) => renderNode(node as any, 0))}
                    </div>
                  )}

                  {/* Staged uploads section */}
                  <div className="mt-4 border rounded-md overflow-hidden">
                    <button
                      className="w-full flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 text-sm font-medium hover:bg-amber-100/60 transition-colors"
                      onClick={() => setStagedExpanded((v) => !v)}
                    >
                      {stagedExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      <span className="text-amber-800 dark:text-amber-300">Portal Uploads — Staged</span>
                      {stagedLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin text-amber-600 ml-1" />
                      ) : (
                        <Badge variant="outline" className="ml-1 text-amber-700 border-amber-400 bg-amber-50 dark:bg-transparent text-[10px] h-4 px-1.5">
                          {stagedUploads.length}
                        </Badge>
                      )}
                      <span className="ml-auto text-xs text-amber-700/70 font-normal">Not yet in SmartVault</span>
                    </button>

                    {stagedExpanded && (
                      <div className="divide-y">
                        {stagedLoading ? (
                          <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                          </div>
                        ) : stagedUploads.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic px-4 py-3">No staged uploads — all portal files have been moved to SmartVault.</p>
                        ) : (
                          stagedUploads.map((upload) => (
                            <div key={upload.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 group">
                              <FileText className="w-4 h-4 text-amber-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm truncate font-medium">{upload.name}</p>
                                {upload.task_title && (
                                  <p className="text-xs text-muted-foreground truncate">Task: {upload.task_title}</p>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground shrink-0">{fmtBytes(upload.file_size)}</span>
                              <span className="text-xs text-muted-foreground shrink-0">{fmtDate(upload.created_at)}</span>
                              <Badge variant="outline" className="text-amber-700 border-amber-400 bg-amber-50 dark:bg-transparent text-[10px] px-1.5 shrink-0">
                                Staged
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 text-xs gap-1 shrink-0 opacity-90 transition-opacity"
                                onClick={() => setMoveStagedDialog({ upload })}
                              >
                                <Move className="w-3 h-3" /> Move to SV
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete file?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Delete <span className="font-medium text-foreground">"{deleteConfirm?.node.name}"</span> from SmartVault? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.node.uri)}
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move SV file dialog */}
      {moveFileDialog && (
        <FolderPickerDialog
          open={true}
          onClose={() => setMoveFileDialog(null)}
          title={`Move "${moveFileDialog.node.name}" to…`}
          confirmLabel="Move File"
          folders={allFolders.filter((f) => f.uri !== moveFileDialog.node.uri)}
          loading={moveMutation.isPending}
          onConfirm={(targetFolderUri) => {
            moveMutation.mutate({ fileUri: moveFileDialog.node.uri, targetFolderUri });
          }}
        />
      )}

      {/* Move staged upload dialog */}
      {moveStagedDialog && (
        <FolderPickerDialog
          open={true}
          onClose={() => setMoveStagedDialog(null)}
          title={`Move "${moveStagedDialog.upload.name}" to SmartVault`}
          confirmLabel="Move to SmartVault"
          folders={allFolders}
          loading={moveStagedMutation.isPending}
          onConfirm={(folderUri) => {
            moveStagedMutation.mutate({
              attachmentId: moveStagedDialog.upload.id,
              folderPath: folderUri,
            });
          }}
        />
      )}
    </>
  );
}
